import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, KeyRound, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../api";
import { CompanyMark } from "../branding/companyBrand";

const MICROSOFT_ERRORS = {
  microsoft_cancelled: "Microsoft sign-in was cancelled.",
  microsoft_expired: "That sign-in attempt expired. Please try again.",
  microsoft_error: "Microsoft sign-in failed. Please try again or contact Admin.",
  domain_not_allowed: "That Microsoft account isn't on the company domain.",
  not_provisioned: "Your account isn't set up in SGP Office OS yet. Ask Admin to add you.",
  account_disabled: "This account has been disabled. Contact Admin.",
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [setupAvailable, setSetupAvailable] = useState(false);
  const [microsoftAvailable, setMicrosoftAvailable] = useState(false);
  const [creating, setCreating] = useState(false);
  const [setupToken, setSetupToken] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/auth/bootstrap-status").then(({ data }) => {
      setSetupAvailable(data.available);
      setMicrosoftAvailable(!!data.microsoft_sso);
      if (data.available && data.email) setEmail(data.email);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const err = searchParams.get("error");
    if (err) {
      toast.error(MICROSOFT_ERRORS[err] || "Sign-in failed. Please try again.");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const signInWithMicrosoft = () => {
    window.location.href = `${API}/auth/microsoft/login`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (creating) {
        if (password !== confirmPassword) throw new Error("Passwords do not match");
        await api.post("/auth/bootstrap", { email, password, setup_token: setupToken });
        setSetupAvailable(false);
        setCreating(false);
        setSetupToken("");
        setConfirmPassword("");
        toast.success("Account created. Sign in to set up 2FA.");
        return;
      }
      const data = await login(email, password);
      if (data.status === "ok") {
        navigate("/workspace", { replace: true });
      } else if (data.status === "2fa_required" || data.status === "2fa_setup_required") {
        navigate("/login/verify", { state: data, replace: true });
      }
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      <motion.div
        initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative hidden lg:flex w-1/2 flex-col justify-between p-12 overflow-hidden"
        style={{ background: "linear-gradient(160deg, #0B1F5C 0%, #102E8C 55%, #1D43B8 100%)" }}
      >
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full border border-white/10" />
        <div className="absolute -right-10 top-20 h-64 w-64 rounded-full border border-white/10" />
        <div className="relative flex items-center gap-3">
          <CompanyMark company={{ id: "sgp", name: "Shree Gauri Pharma", short_name: "SGP" }} className="h-16 w-16" imageClassName="h-14 w-14" />
          <p className="text-white font-heading font-bold">SGP Office OS</p>
        </div>
        <div className="relative">
          <p className="text-[11px] uppercase tracking-widest text-gold-400 font-bold mb-3">SGP Office OS</p>
          <h1 className="text-4xl font-heading font-extrabold text-white leading-tight mb-4">
            Wholesale Dealer &amp;<br />Government Order<br /><span className="text-gold-400">Supplier.</span>
          </h1>
          <p className="text-sm text-slate-300 max-w-sm mb-8">
            One secure login for multiple companies — billing, stock, accounts, GST, tenders and letters,
            secured for every person, visible to the right people only.
          </p>
          <div className="flex gap-3">
            {[
              { icon: KeyRound, label: "2-Factor", sub: "Login protection" },
              { icon: ShieldCheck, label: "Role-based", sub: "Access by module" },
              { icon: ScrollText, label: "Audit Trail", sub: "Every action logged" },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="rounded-lg bg-white/10 border border-white/10 px-3.5 py-2.5">
                <Icon className="h-4 w-4 text-gold-400 mb-1.5" />
                <p className="text-xs font-bold text-white">{label}</p>
                <p className="text-[10px] text-slate-400">{sub}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-[11px] text-slate-500">&copy; {new Date().getFullYear()} SGP Office OS</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: "easeOut", delay: 0.1 }}
        className="flex-1 flex items-center justify-center px-4 py-10 lg:bg-white"
      >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6 lg:hidden">
          <div className="mb-3"><CompanyMark company={{ id: "sgp", name: "Shree Gauri Pharma", short_name: "SGP" }} className="h-20 w-20" imageClassName="h-[4.5rem] w-[4.5rem]" /></div>
          <h1 className="font-heading font-bold text-white text-lg">SGP Office OS</h1>
          <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">SGP Office OS</p>
        </div>
        <div className="hidden lg:block mb-6">
          <h2 className="font-heading font-bold text-slate-900 text-2xl">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1">Sign in once, then choose your company and financial year.</p>
        </div>
        <div className="sgp-card p-6 space-y-4">
          {microsoftAvailable && (
            <>
              <button type="button" onClick={signInWithMicrosoft} data-testid="microsoft-signin-btn"
                className="w-full h-10 rounded-md bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                Sign in with Microsoft
              </button>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500">
                <div className="h-px flex-1 bg-slate-800" /> or <div className="h-px flex-1 bg-slate-800" />
              </div>
            </>
          )}
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="sgp-label">Email</label>
            <input
              type="email" required autoFocus data-testid="login-email"
              className="sgp-input" placeholder="you@shreegauripharma.in"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="sgp-label">Password</label>
            <input
              type="password" required data-testid="login-password"
              className="sgp-input" placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {creating && (
            <>
              <div>
                <label className="sgp-label">Confirm password</label>
                <input type="password" required minLength={10} className="sgp-input"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
              <div>
                <label className="sgp-label">One-time setup code</label>
                <input type="password" required className="sgp-input"
                  value={setupToken} onChange={(e) => setSetupToken(e.target.value)} />
              </div>
            </>
          )}
          <button
            type="submit" disabled={busy} data-testid="login-submit"
            className="w-full h-10 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
          >
            <Lock className="h-4 w-4" /> {busy ? "Checking…" : creating ? "Create first account" : "Sign in"}
          </button>
          {setupAvailable && (
            <button type="button" className="w-full text-sm text-teal-400 hover:underline"
              onClick={() => setCreating(!creating)}>
              {creating ? "Back to sign in" : "First user? Create your account"}
            </button>
          )}
          {creating && <p className="text-xs text-slate-400">Only the designated company Admin can create the first account. Ask for the one-time setup code.</p>}
          {!creating && !setupAvailable && <p className="text-xs text-slate-400 text-center">New team member? Ask the Admin for an invite link. Accounts are limited to @shreegauripharma.in.</p>}
          <p className="flex items-center gap-1.5 text-[11px] text-slate-400 justify-center pt-1">
            <ShieldCheck className="h-3.5 w-3.5" /> Only company accounts can sign in · protected by 2FA
          </p>
        </form>
        </div>
      </div>
      </motion.div>
    </div>
  );
}
